// İSG uzmanı portalı — İzin detayı: KKD (Kişisel Koruyucu Donanım) snapshot listesi (api-contract
// §24.3 + SPEC §2.1). İzin açılışında tipteki KKD **izne kopyalanır** (snapshot); İSG uzmanı bu
// izin-kopyasına firma KKD kataloğundan **ekler/siler** (tipten bağımsız; her değişiklik Aksiyonlar'a
// SystemLog — backend). Okuma `GET /permits/{uuid}` (`requiredProtectiveEquipment`). Aynı liste
// taşeron detayında read-only görünür (§21). Ekle/sil uçları backend 093 §6 (sözleşme bekliyor) —
// uç canlı değilse hata görünür, mock yok. Yetki backend'de (reviewer); yetkisiz 403 hata.

import {
  Alert,
  Button,
  ConfirmDialog,
  EmptyState,
  type ProtectiveEquipmentRefDto,
  Select,
  type SelectOption,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableMessageRow,
  TableRow,
  permitsApi,
  protectiveEquipmentApi,
  resolveApiErrorMessage,
} from '@isgportal/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { HardHat, Plus, Trash2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { permitsKeys } from '../../lib/permitsKeys';

/** KKD kataloğunu tek sayfada besleyecek makul üst sınır (tenant ölçeği). */
const OPTIONS_PAGE_SIZE = 200;

interface PermitProtectiveEquipmentPanelProps {
  permitUuid: string;
  /** İznin KKD snapshot'ı (§24.3/§2.1; bağlanmamışsa `[]`). */
  protectiveEquipment: ProtectiveEquipmentRefDto[];
}

export function PermitProtectiveEquipmentPanel({
  permitUuid,
  protectiveEquipment,
}: PermitProtectiveEquipmentPanelProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState('');
  const [removeTarget, setRemoveTarget] = useState<ProtectiveEquipmentRefDto | null>(null);

  // Firma KKD kataloğu (§24 `GET /protective-equipment`) — snapshot'a eklenebilecekler.
  const catalogQuery = useQuery({
    queryKey: ['permits', 'options', 'protectiveEquipment'],
    queryFn: () =>
      protectiveEquipmentApi.listProtectiveEquipment({ page: 1, pageSize: OPTIONS_PAGE_SIZE }),
    staleTime: 60_000,
  });

  const addMutation = useMutation({
    // Snapshot değer kopyası — izne katalog ögesinin **adı** gönderilir (api-contract §30.6; FK değil).
    mutationFn: (name: string) => permitsApi.addPermitProtectiveEquipment(permitUuid, { name }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: permitsKeys.detail(permitUuid) });
      setSelected('');
    },
  });

  const removeMutation = useMutation({
    mutationFn: (peUuid: string) => permitsApi.removePermitProtectiveEquipment(permitUuid, peUuid),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: permitsKeys.detail(permitUuid) });
      setRemoveTarget(null);
    },
  });

  // Zaten snapshot'ta olanları katalog seçeneklerinden çıkar (tekrar ekleme sunulmaz). Snapshot
  // satırının uuid'i katalogla ilişkisiz (per-permit Guid; §30.6) — eşleştirme **ad** üzerinden yapılır.
  const existingNames = useMemo(
    () => new Set(protectiveEquipment.map((p) => p.name)),
    [protectiveEquipment],
  );
  const catalogOptions: SelectOption[] = useMemo(
    () =>
      (catalogQuery.data?.items ?? [])
        .filter((pe) => !existingNames.has(pe.name))
        .map((pe) => ({ value: pe.uuid, label: pe.name })),
    [catalogQuery.data, existingNames],
  );

  return (
    <div className="flex flex-col gap-3">
      {/* İSG uzmanı KKD ekler (SPEC §2.1) — katalogdan seç + Ekle. Yetki backend'de. Dolu (primary)
          düğme → ghost (S33 Slice 6, coordinator bulgusu — bkz. `PermitVehiclesPanel.tsx`'in AYNI
          notu); kutulu (`border-dashed`) şerit KALKAR, Tile'a bitişik (`TableToolbar`'ın 48px
          şeridiyle AYNI yükseklik/kenarlık) bir üst şerit olur. */}
      <div className="flex flex-col gap-2 border-b border-border px-1 py-2 carbon:px-4">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <p className="text-sm text-muted carbon:text-xs sm:mr-2">{t('permits.ppe.subtitle')}</p>
          <div className="sm:flex-1">
            <Select
              aria-label={t('permits.ppe.add')}
              disabled={catalogQuery.isPending || addMutation.isPending}
              placeholder={t('permits.ppe.addPlaceholder')}
              options={catalogOptions}
              value={selected}
              onChange={(e) => setSelected(e.target.value)}
            />
          </div>
          <Button
            variant="ghost"
            size="sm"
            loading={addMutation.isPending}
            disabled={selected === '' || addMutation.isPending}
            onClick={() => {
              // Seçim katalog uuid'ini tutar; mutate anında adı kataloğdan çözüp gönderiyoruz.
              const item = catalogQuery.data?.items.find((pe) => pe.uuid === selected);
              if (item) {
                addMutation.mutate(item.name);
              }
            }}
          >
            <Plus className="h-4 w-4" aria-hidden="true" />
            {t('permits.ppe.add')}
          </Button>
        </div>
        {catalogQuery.isSuccess && catalogOptions.length === 0 ? (
          <p className="text-xs text-muted">{t('permits.ppe.addEmpty')}</p>
        ) : null}
        {addMutation.isError ? (
          <Alert tone="danger">{resolveApiErrorMessage(addMutation.error, t)}</Alert>
        ) : null}
      </div>

      <Table caption={t('permits.detail.section.ppe')}>
        <TableHeader>
          <TableRow>
            <TableHead>{t('permits.ppe.col.name')}</TableHead>
            <TableHead className="w-12">
              <span className="sr-only">{t('permits.common.actions')}</span>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {protectiveEquipment.length === 0 ? (
            <TableMessageRow colSpan={2}>
              <EmptyState icon={HardHat} title={t('permits.ppe.empty')} />
            </TableMessageRow>
          ) : (
            protectiveEquipment.map((item) => (
              <TableRow key={item.uuid}>
                <TableCell className="font-medium text-fg">{item.name}</TableCell>
                <TableCell>
                  <div className="flex items-center justify-end">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setRemoveTarget(item)}
                      disabled={removeMutation.isPending}
                      aria-label={t('permits.ppe.remove', { name: item.name })}
                    >
                      <Trash2 className="h-4 w-4 text-danger" aria-hidden="true" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>

      <ConfirmDialog
        open={removeTarget !== null}
        onOpenChange={(open) => !open && setRemoveTarget(null)}
        title={t('permits.ppe.remove.title')}
        body={t('permits.ppe.remove.body', { name: removeTarget?.name ?? '' })}
        confirmLabel={t('permits.ppe.remove.confirm')}
        confirmVariant="danger"
        loading={removeMutation.isPending}
        error={removeMutation.isError ? removeMutation.error : undefined}
        onConfirm={() => {
          if (removeTarget) {
            removeMutation.mutate(removeTarget.uuid);
          }
        }}
      />
    </div>
  );
}
